/**
 * Typing Indicator Component
 * Displays animated typing indicator when AI is generating a response
 */

import React from 'react'
import { Bot } from 'lucide-react'

interface TypingIndicatorProps {
  className?: string
  showAvatar?: boolean
  avatarSize?: 'sm' | 'md' | 'lg'
}

export const TypingIndicator: React.FC<TypingIndicatorProps> = ({
  className = '',
  showAvatar = true,
  avatarSize = 'md'
}) => {
  const avatarSizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10'
  }

  return (
    <div className={`flex items-center gap-3 animate-fade-in ${className}`}>
      {showAvatar && (
        <div className={`flex-shrink-0 ${avatarSizeClasses[avatarSize]} rounded-full bg-primary/10 flex items-center justify-center`}>
          <Bot className="w-4 h-4 text-primary" />
        </div>
      )}
      
      <div className="flex items-center gap-3 px-4 py-3 bg-card rounded-2xl border border-border shadow-sm">
        <div className="flex items-center gap-1">
          <span className="typing-dot" style={{ animationDelay: '0ms' }} />
          <span className="typing-dot" style={{ animationDelay: '150ms' }} />
          <span className="typing-dot" style={{ animationDelay: '300ms' }} />
        </div>
        <span className="text-xs text-muted-foreground">AI is thinking...</span>
      </div>
    </div>
  )
}

/**
 * Compact Typing Indicator
 * Minimal version without text
 */
export const CompactTypingIndicator: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`inline-flex items-center gap-1 px-3 py-2 bg-muted rounded-full ${className}`}>
      <span className="typing-dot-small" style={{ animationDelay: '0ms' }} />
      <span className="typing-dot-small" style={{ animationDelay: '150ms' }} />
      <span className="typing-dot-small" style={{ animationDelay: '300ms' }} />
    </div>
  )
}

/**
 * Inline Typing Indicator
 * For use within message bubbles
 */
export const InlineTypingIndicator: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <span className="typing-dot-small" style={{ animationDelay: '0ms' }} />
      <span className="typing-dot-small" style={{ animationDelay: '150ms' }} />
      <span className="typing-dot-small" style={{ animationDelay: '300ms' }} />
    </div>
  )
}

export default TypingIndicator
